import axios, { type AxiosInstance } from 'axios';
import { useMemo } from 'react';
import type {
  Service,
  CreateIncidentInput,
  Incident,
  Announcement,
  ServiceDetailsResponse,
  SummaryResponse,
  QuickTicketInput,
} from '@incidents/contracts';
import { useTenant } from './tenant.tsx';

export function createApiClient(apiKey: string): AxiosInstance {
  const clientTZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const instance = axios.create({ baseURL: '/api', timeout: 10_000 });
  instance.interceptors.request.use((cfg) => {
    cfg.headers['X-API-Key'] = apiKey;
    cfg.headers['X-TZ'] = clientTZ;
    return cfg;
  });
  return instance;
}

export function useApi(): AxiosInstance {
  const tenant = useTenant();
  return useMemo(() => createApiClient(tenant.apiKey), [tenant.apiKey]);
}

// Typed wrappers — keep return shapes anchored to @incidents/contracts.
export const apiCalls = {
  listServices: (api: AxiosInstance) => api.get<Service[]>('/services').then((r) => r.data),

  getServiceDetails: (api: AxiosInstance, name: string) =>
    api.get<ServiceDetailsResponse>(`/services/${encodeURIComponent(name)}`).then((r) => r.data),

  summary: (api: AxiosInstance) => api.get<SummaryResponse>('/summary').then((r) => r.data),

  listAnnouncements: (api: AxiosInstance) =>
    api.get<Announcement[]>('/announcements').then((r) => r.data),

  createIncident: (api: AxiosInstance, input: CreateIncidentInput) =>
    api.post<Incident>('/incidents', input).then((r) => r.data),

  resolveIncident: (api: AxiosInstance, id: number) =>
    api.patch<{ ok: true }>(`/incidents/${id}/resolve`, {}).then((r) => r.data),

  postTz: (api: AxiosInstance, tz: string) =>
    api.post<{ ok: true; tz: string }>('/tz', { tz }).then((r) => r.data),

  quickTicket: (api: AxiosInstance, input: QuickTicketInput) =>
    api
      .post<{ ok: boolean; data?: unknown; error?: string }>('/support/quick-ticket', input)
      .then((r) => r.data),

  fetchI18n: async (lang: string): Promise<Record<string, unknown>> => {
    const r = await axios.get(`/i18n/${lang}.json`, {
      headers: { 'X-API-Key': window.__TENANT__?.apiKey ?? '' },
    });
    return r.data;
  },
};
